import { DataGrid } from "@mui/x-data-grid";
import {
  Card,
  CardHeader,
  CardContent,
  useMediaQuery,
  Typography,
  Box,
  Grid2 as Grid,
  Tabs,
  Tab,
  Snackbar,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import axios from "axios";
import { green, grey, red } from "@mui/material/colors";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoadingButton } from "@mui/lab";
import DownloadIcon from "@mui/icons-material/Download";
import {
  getPermitPackages,
  handleDownloadNoc,
  setPrevTourStep,
  updatePermitPackage,
} from "../../store/actions/dataActions";
import { useTour } from "@reactour/tour";
import { tourSteps } from "../../data/walkthroughDemo/steps";
import { notarizationRequestsColumn } from "./permitPackageTabs/permitTypeColumns";
import {
  noRecordTitle,
  packageRowDetailsEmptyTitle,
} from "../../data/mappings/permitPackageMappings";
import SelectDownloadFileSIzeModal from "../modals/SelectDownloadFileSIzeModal";
import {
  setActiveTab,
  setLeftValue,
  setMyPermitsSnackOpen,
  setNumCreated,
  setPage,
  setPageSize,
  setPermitLoadings,
  setRightValue,
  setRowCount,
  setSelectedRow,
} from "../../store/actions/uiActions/myPermitsActions";
import CustomDocumentMiddleContent from "./middleContent/CustomDocumentMiddleContent";
import PermitPackageMiddleContent from "./middleContent/PermitPackageMiddleContent";
import CustomDocumentRightContent from "./rightContent/CustomDocumentRightContent";
import PermitPackageRightContent from "./rightContent/PermitPackageRightContent";
import { formatCity, formatDateFromUnix } from "./permitComponentHelper";

const NotarizationRequestsGrid = ({
  setDocNotaryTypeValue,
  docNotaryTypeValue,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const permitPackages = useSelector((state) => state.nocData?.permitPackages);
  const { cancelPermitLoading, ppDownloading } = useSelector(
    (state) => state.myPermitsComponent.permits.loadings,
  );

  const { myPermitsSnackOpen, myPermitsSnackText, numCreated } = useSelector(
    (state) => state.myPermitsComponent.permits,
  );

  const { rightValue, activeTab, selectedRow, leftValue } = useSelector(
    (state) => state.myPermitsComponent,
  );

  const { permitDrawerOpen } = useSelector(
    (state) => state.myPermitsComponent.permits.modalStates,
  );

  const { dataLoading, activeTabLoading, tempLoading } = useSelector(
    (state) => state.myPermitsComponent.permits.loadings,
  );
  const { page, pageSize, rowCount } = useSelector(
    (state) => state.myPermitsComponent.permits.pagination,
  );

  const prevRowId = useRef(null);

  const [fileSizeModalOpen, setFileSizeModalOpen] = useState(false);

  const dispatch = useDispatch();

  const [recordLoading, setRecordLoading] = useState(false);
  const [currentLeftNotarized, setCurrentLeftNotarized] = useState(false);
  const [currentLeftInProgress, setCurrentLeftInProgress] = useState(false);
  const [mobileDetailsView, setMobileDetailsView] = useState("docs");

  const { isOpen, currentStep, setCurrentStep } = useTour();

  const sm = useMediaQuery((theme) => theme.breakpoints.up("sm"));
  const lg = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  const isMobileRequest = useMediaQuery("(max-width:900px)");
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const desktopSize = useMediaQuery((theme) => theme.breakpoints.down("xl"));

  const selectedDocsLength = selectedRow?.selected_documents?.length;

  const fetchDataRef = useRef(null);

  useEffect(() => {
    // Get the current document name and its contractor owner
    const currentDocName = selectedRow?.selected_documents?.[leftValue]?.name;
    const currentDocData = currentDocName
      ? selectedRow?.documents?.[currentDocName]
      : null;
    const docContractorLicense = currentDocData?.contractor;

    // Check if this document belongs to an additional contractor
    const additionalContractors =
      selectedRow?.processing_permit?.notarization?.additional_contractors ||
      [];
    const additionalContractorForDoc = additionalContractors.find(
      (ac) => ac?.license_number === docContractorLicense,
    );

    // Determine if the document is notarized based on who owns it
    let notarized = false;

    if (additionalContractorForDoc) {
      // Document belongs to an additional contractor - check their status
      notarized =
        additionalContractorForDoc?.notarization?.is_notarized === true ||
        additionalContractorForDoc?.notarization?.is_signed === true;
    } else {
      // Document belongs to main contractor (or no specific contractor) - use standard logic
      notarized =
        selectedRow?.notarized ||
        (leftValue === 0 && selectedRow?.noc_notarized === true) ||
        (leftValue === 1 &&
          selectedRow?.noc_notarized === true &&
          selectedRow?.documents?.noc_affidavit?.is_created === true) ||
        (leftValue >= 1 && selectedRow?.pp_notarized === true)
          ? true
          : false;
    }

    if (notarized !== currentLeftNotarized) {
      setCurrentLeftNotarized(notarized);
    }

    const notarizingDocs =
      selectedRow?.processing_permit?.notarization?.notarizing_docs;
    const finalized = selectedRow?.processing_permit?.notarization?.finalized;
    const started = selectedRow?.processing_permit?.notarization?.started;

    const inProgress =
      notarizingDocs === 0 && started === true && !finalized
        ? true
        : (notarizingDocs === 1 &&
              leftValue === 0 &&
              started === true &&
              !finalized) ||
            (notarizingDocs === 1 &&
              selectedRow?.selected_documents?.[leftValue]?.name ===
                "noc_affidavit" &&
              started === true &&
              !finalized)
          ? true
          : notarizingDocs === 2 &&
              leftValue !== 0 &&
              started === true &&
              !finalized
            ? true
            : false;

    const currentProgress = !notarized && inProgress ? true : false;

    if (currentLeftInProgress !== currentProgress) {
      setCurrentLeftInProgress(currentProgress);
    }
  }, [leftValue, selectedRow, currentLeftInProgress, currentLeftNotarized]);

  const snackHandleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    dispatch(setMyPermitsSnackOpen({ isOpen: false, snackText: "" }));
  };

  // const handleRightChange = (event, newValue) => {
  //   if (isOpen) {
  //     return;
  //   }
  //   setIsFading(true);
  //   setTimeout(() => {
  //     if (rightValue !== newValue) {
  //       props?.setRightValue(newValue);
  //       setIsFading(false);
  //     }
  //   }, 300); // Duration should match the CSS transition duration
  // };

  const handleLeftChange = useCallback(
    (event, newValue, shouldMoveToStatus = false) => {
      if (leftValue !== newValue) {
        if (selectedDocsLength === 1) {
          dispatch(setLeftValue(0));
        } else {
          dispatch(setLeftValue(newValue));
        }

        if (shouldMoveToStatus) {
          setMobileDetailsView("status");
        }
      }
    },
    [leftValue, selectedDocsLength, dispatch],
  );

  const mobileDocTabs = useMemo(() => {
    if (!selectedRow || selectedRow?.cst_packageID) return null;

    return (
      <Tabs
        value={leftValue}
        onChange={(e, value) => handleLeftChange(e, value, true)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        orientation="vertical"
        aria-label="document tabs"
        slotProps={{
          indicator: {
            sx: {
              bottom: 0,
              height: "3px",
            },
          },
        }}
        sx={{
          width: "100%",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          px: 1,
          "& .MuiTab-root": {
            textTransform: "none",
            alignItems: "flex-start",
            minHeight: 40,
            px: 1.5,
            marginLeft: 0,
          },
        }}
      >
        {selectedRow?.selected_documents?.map((document, index) => {
          const isEnabled =
            selectedRow?.permit_package_created ||
            document?.created ||
            (selectedRow?.selected_documents[index - 1]?.created &&
              !document?.created);

          return (
            <Tab
              disabled={!isEnabled}
              key={document?.name || index}
              label={document?.label}
              disableRipple
            />
          );
        })}
      </Tabs>
    );
  }, [selectedRow, leftValue, handleLeftChange]);

  const middleContent = useMemo(() => {
    if (selectedRow?.cst_packageID) {
      return (
        <CustomDocumentMiddleContent
          setFileSizeModalOpen={setFileSizeModalOpen}
          isMobile={isMobileRequest}
        />
      );
    } else {
      return (
        <PermitPackageMiddleContent
          currentLeftNotarized={currentLeftNotarized}
          currentLeftInProgress={currentLeftInProgress}
          setFileSizeModalOpen={setFileSizeModalOpen}
          isMobile={isMobileRequest}
        />
      );
    }
  }, [
    setFileSizeModalOpen,
    currentLeftNotarized,
    currentLeftInProgress,
    selectedRow?.cst_packageID,
    isMobileRequest,
  ]);

  const fetchData = useCallback(
    async (newPage, newRowsPerPage) => {
      try {
        await dispatch(setPermitLoadings({ dataLoading: true }));
        const result = await dispatch(
          getPermitPackages({
            newPage: newPage,
            newPageSize: newRowsPerPage,
            customActiveTab: activeTab,
          }),
        );

        // Update rowCount if the request was successful
        if (result && result.success) {
          const rowCount = typeof result.data === "number" ? result.data : 0;
          dispatch(setRowCount(rowCount));
        }
      } catch (error) {
        console.error("Error fetching permit packages:", error);
      } finally {
        await dispatch(setPermitLoadings({ dataLoading: false }));
      }
    },
    [dispatch, activeTab],
  );

  // Ensure we fetch data with the correct activeTab when component mounts
  // This fixes the timing issue where data is fetched before activeTab is set to 3
  useEffect(() => {
    if (activeTab === 3 || activeTab === 4) {
      if (!fetchDataRef?.current) {
        fetchDataRef.current = true;
        fetchData(0, 5);
      }
    }
  }, [activeTab, fetchData]);

  const handlePaginationModelChange = useCallback(
    (model, details) => {
      const newPage = model.page;
      const newRowsPerPage = model.pageSize;
      dispatch(setPage(newPage));
      dispatch(setPageSize(newRowsPerPage));
      dispatch(setSelectedRow(null));
      fetchData(newPage, newRowsPerPage);
    },
    [fetchData, dispatch],
  );

  const handleRowClick = useCallback(
    (params, event, details) => {
      if (cancelPermitLoading) {
        return;
      }
      const row = params.row;

      if (row?.cst_packageID) {
        if (selectedRow?.id !== row?.id && !isOpen) {
          dispatch(setSelectedRow(params.row));
        }
      } else {
        if (isOpen) {
          dispatch(setPrevTourStep(currentStep));
          setTimeout(() => {
            dispatch(setSelectedRow(params.row));
            setCurrentStep((s) => (s === tourSteps?.length - 1 ? 0 : s + 1));
          }, 200);
        }

        // Only update selectedRow if it's different from the current one
        if (selectedRow?.id === row?.id) {
          return;
        }
        dispatch(setPermitLoadings({ tempLoading: false }));
        const newLeftValue = row?.selected_documents?.length - 1;

        if (selectedDocsLength - 1 > newLeftValue) {
          handleLeftChange(null, newLeftValue);
        } else if (
          row?.selected_documents?.length < leftValue + 1 &&
          newLeftValue !== leftValue
        ) {
          handleLeftChange(null, newLeftValue);
        }

        if (isOpen) {
          dispatch(setRightValue(0));
        } else if (activeTab !== 1 && !row?.assigned_package?.is_assigned) {
          const newRightValue =
            (row?.permit_package_created ||
              (row?.noc_created && row?.selected_documents?.length === 1)) &&
            !row?.notarized
              ? 2
              : row?.permit_package_created && row?.notarized && !row?.recorded
                ? 3
                : row?.notarized &&
                    !row?.recorded &&
                    (!row?.processing_permit?.recording?.is_recorded ||
                      row?.processing_permit?.recording?.recording_failed)
                  ? 3
                  : 0;

          if (newRightValue !== rightValue && !row?.cst_packageID) {
            dispatch(setRightValue(newRightValue));
          }
        } else {
          if (rightValue !== 0 && !row?.cst_packageID) {
            dispatch(setRightValue(0));
          }
        }

        // Only update selectedRow if it's different from the current one
        if (selectedRow?.id !== row?.id && !isOpen) {
          dispatch(setSelectedRow(row));
        }
      }
    },
    [
      isOpen,
      currentStep,
      dispatch,
      setCurrentStep,
      activeTab,
      handleLeftChange,
      leftValue,
      rightValue,
      selectedRow?.id,
      selectedDocsLength,
      cancelPermitLoading,
    ],
  );

  useEffect(() => {
    if (selectedRow) {
      // Set tempLoading to false if necessary
      if (tempLoading !== false) {
        dispatch(setPermitLoadings({ tempLoading: false }));
      }

      // Update selectedRow only if necessary
      let updatedRow = {};

      if (selectedRow?.cst_packageID) {
        updatedRow = permitPackages.find(
          (item) => item.cst_packageID === selectedRow.cst_packageID,
        );
      } else {
        updatedRow = permitPackages.find(
          (item) => item.task_id === selectedRow.task_id,
        );
      }

      if (updatedRow && updatedRow !== selectedRow) {
        dispatch(setSelectedRow(updatedRow));
      }
    }
  }, [permitPackages, tempLoading, dispatch, selectedRow]);

  useEffect(() => {
    if (selectedRow) {
      const createdCount = selectedRow?.selected_documents?.filter(
        (doc) => doc.created === true,
      ).length;

      if (numCreated !== createdCount) {
        dispatch(setNumCreated(createdCount));
      }
    }
  }, [selectedRow, numCreated, dispatch]);

  useEffect(() => {
    if (!selectedRow || permitDrawerOpen) return;

    if (prevRowId.current === selectedRow.id) return; // ⛔ skip if same row

    prevRowId.current = selectedRow.id; // ✅ mark this row as seen

    if (selectedRow && !permitDrawerOpen) {
      // If permit_package_created is true, ensure leftValue is 0
      if (selectedRow?.permit_package_created) {
        if (leftValue !== 0) {
          dispatch(setLeftValue(0));
        }
      } else {
        const selectedDocs = selectedRow.selected_documents;
        let lastCreatedIndex = 0;

        for (let i = 0; i < selectedDocs?.length; i++) {
          const doc = selectedDocs?.[i];
          if (doc?.created) {
            lastCreatedIndex = i + 1;
          } else {
            break;
          }
        }

        // Only set leftValue if there’s more than one document
        if (selectedDocs?.length > 1 && leftValue !== lastCreatedIndex) {
          if (lastCreatedIndex > selectedDocs?.length - 1) {
            handleLeftChange(null, lastCreatedIndex - 1);
          } else {
            handleLeftChange(null, lastCreatedIndex);
          }
        } else if (selectedDocs?.length === 1) {
          dispatch(setLeftValue(0));
        }
      }
    }
  }, [selectedRow, permitDrawerOpen, handleLeftChange, leftValue, dispatch]);

  const compactStatusChip = (label, value, allowNA = false) => {
    const isNA = allowNA && value === null;

    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}>
        <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}>
          {label}
        </Typography>
        {isNA ? (
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}>
            N/A
          </Typography>
        ) : value ? (
          <CheckCircleIcon sx={{ color: green[500], fontSize: "1rem" }} />
        ) : (
          <CancelIcon sx={{ color: red[500], fontSize: "1rem" }} />
        )}
      </Box>
    );
  };

  const permitColumnsSmallByTab = useMemo(() => {
    return [
      {
        field: "mobile",
        headerName: "Packages",
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const row = params?.row || {};
          const isCustomDoc = Boolean(row?.cst_packageID);

          const packageName = isCustomDoc
            ? "Custom Documents"
            : row?.package_name || "N/A";

          const projectAddress = isCustomDoc
            ? row?.document_location?.project_address || "N/A"
            : row?.project_address || "N/A";

          const county = isCustomDoc
            ? row?.document_location?.county || "N/A"
            : row?.county || "N/A";

          const city = isCustomDoc ? "N/A" : formatCity(row?.city);
          const notarized = isCustomDoc
            ? row?.document?.actions?.notarize
              ? Boolean(row?.notarized)
              : null
            : Boolean(row?.notarized);

          const recorded = isCustomDoc
            ? row?.document?.actions?.record
              ? Boolean(
                  row?.processing_permit?.recording?.recorded || row?.recorded,
                )
              : null
            : Boolean(row?.recorded);

          // Tab-specific: "Sent to Contractors"
          const assignedTo = row?.assigned_package?.user_email || "N/A";
          const sentDate = row?.assigned_package?.assigned_at
            ? formatDateFromUnix(row?.assigned_package?.assigned_at, "short")
            : "N/A";

          return (
            <Box
              sx={{
                width: "100%",
                py: 0.6,
                px: 1,
                display: "flex",
                flexDirection: "column",
                gap: 0.45,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: 700,
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                    }}
                  >
                    {packageName}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "rgba(0,0,0,0.62)",
                      lineHeight: 1.2,
                      mt: 0.25,
                      wordBreak: "break-word",
                    }}
                  >
                    {projectAddress}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.74rem",
                      color: "rgba(0,0,0,0.62)",
                      lineHeight: 1.2,
                      mt: 0.15,
                    }}
                  >
                    {county} • {city}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "rgba(0,0,0,0.62)",
                    flexShrink: 0,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {sentDate}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "0.74rem",
                  color: "rgba(0,0,0,0.62)",
                  lineHeight: 1.2,
                  mt: 0.15,
                }}
              >
                {assignedTo}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.0 }}>
                {compactStatusChip("Notarized", notarized, true)}
                {compactStatusChip("Recorded", recorded, true)}
              </Box>
            </Box>
          );
        },
      },
    ];
  }, []);

  const updateSelectedPackageTypeData = useCallback(
    async (index) => {
      try {
        await dispatch(
          setPermitLoadings({ activeTabLoading: index, dataLoading: true }),
        );
        const result = await dispatch(
          getPermitPackages({ newPage: 0, customActiveTab: index }),
        );

        // Update rowCount if the request was successful
        if (result && result.success) {
          const rowCount = typeof result.data === "number" ? result.data : 0;
          dispatch(setRowCount(rowCount));
        }
      } catch (error) {
        console.error("Error updating package type data:", error);
      } finally {
        await dispatch(
          setPermitLoadings({ activeTabLoading: null, dataLoading: false }),
        );
      }
    },
    [dispatch],
  );

  const handlePackageTypeChange = useCallback(
    async (index) => {
      if (activeTab === index) return;

      dispatch(setActiveTab(index));
      dispatch(setSelectedRow(null));
      setPage(0);
      updateSelectedPackageTypeData(index);
    },
    [activeTab, updateSelectedPackageTypeData, dispatch],
  );

  const handleStartRecord = useCallback(async () => {
    try {
      setRecordLoading(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/building/start-manual-recording`,
        {
          task_id: selectedRow?.task_id,
          currentSession,
        },
        {
          withCredentials: true,
        },
      );
      if (resp?.data?.success) {
        await dispatch(updatePermitPackage(resp?.data?.data));
      }
    } catch (error) {
      // optional: add error handling here
    } finally {
      setRecordLoading(false);
    }
  }, [selectedRow?.task_id, currentSession, dispatch]);

  const memoizedRightContent = useMemo(() => {
    if (!selectedRow) return null;

    return selectedRow?.cst_packageID ? (
      <CustomDocumentRightContent
        recordLoading={recordLoading}
        handleStartRecord={handleStartRecord}
        isMobile={isMobileRequest}
      />
    ) : (
      <PermitPackageRightContent
        recordLoading={recordLoading}
        handleStartRecord={handleStartRecord}
        setDocNotaryTypeValue={setDocNotaryTypeValue}
        docNotaryTypeValue={docNotaryTypeValue}
        isMobile={isMobileRequest}
        isDesktop={desktopSize}
      />
    );
  }, [
    docNotaryTypeValue,
    handleStartRecord,
    recordLoading,
    selectedRow,
    setDocNotaryTypeValue,
    isMobileRequest,
    desktopSize,
  ]);

  return (
    <>
      <Card
        sx={{
          position: "relative",
          mb: 0,
          pb: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: isMobileRequest ? 1 : 0,
            px: 1,
            pb: 2,
          }}
        >
          <CardHeader
            title={
              sm
                ? `${" \u00A0\u00A0"}N O T A R I Z A T I O N${" \u00A0\u00A0"}R E Q U E S T S`
                : `Notarization Requests`
            }
            sx={{
              flexGrow: 1,
              pt: isMobileRequest ? "16px" : "16px",
              px: 0,
              textAlign: "left",
            }} // This ensures the title takes up available space
          />
          <Box
            mr={1}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              width: isMobileRequest ? "100%" : "auto",
              justifyContent: isMobileRequest ? "center" : "flex-start",
            }}
          >
            {isMobileRequest ? (
              <Tabs
                value={activeTab}
                onChange={(_, idx) => handlePackageTypeChange(idx)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  width: "100%",
                  minHeight: 36,
                  "& .MuiTab-root": {
                    minHeight: 36,
                    textTransform: "none",
                    fontWeight: 700,
                    px: 1.25,
                    py: 0,
                  },
                }}
                slotProps={{
                  indicator: { sx: { height: "3px" } },
                }}
              >
                <Tab
                  value={3}
                  label="Active Requests"
                  disabled={Boolean(activeTabLoading) && activeTabLoading === 4}
                />

                <Tab
                  value={4}
                  label="Archived Requests"
                  disabled={Boolean(activeTabLoading) && activeTabLoading === 3}
                />
              </Tabs>
            ) : (
              <>
                <LoadingButton
                  disabled={Boolean(activeTabLoading) && activeTabLoading === 4}
                  loading={activeTabLoading === 3}
                  variant={activeTab === 3 ? "contained" : "outlined"}
                  size="small"
                  sx={{ mr: 0.5 }}
                  onClick={() => handlePackageTypeChange(3)}
                >
                  Active Requests
                </LoadingButton>
                <LoadingButton
                  disabled={Boolean(activeTabLoading) && activeTabLoading === 3}
                  loading={activeTabLoading === 4}
                  size="small"
                  variant={activeTab === 4 ? "contained" : "outlined"}
                  onClick={() => handlePackageTypeChange(4)}
                >
                  Archived Requests
                </LoadingButton>
              </>
            )}
          </Box>
        </Box>

        <CardContent
          className="bottomCard-6"
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            <Box
              className="step13"
              sx={{
                height: "100%",
                borderBottom: "5px solid #d3d3d3",
              }}
            >
              {permitPackages?.length === 0 ? (
                <Box
                  sx={{
                    dispay: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: { xs: "250px", xl: "auto" },
                  }}
                >
                  <Typography
                    mt={2}
                    align="center"
                    color="primary"
                    variant="h6"
                  >
                    {noRecordTitle?.[activeTab]}
                  </Typography>
                </Box>
              ) : (
                <DataGrid
                  sx={{
                    height: "100%",
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    "& .MuiDataGrid-main": { flex: 1, minHeight: 0 },
                    "& .MuiDataGrid-virtualScroller": {
                      flex: 1,
                      minHeight: 0,
                    },
                    "& .wrapHeader .MuiDataGrid-columnHeaderTitle": {
                      whiteSpace: "normal",
                      lineHeight: 1.2,
                      textAlign: "center",
                    },
                    "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                      outline: "none !important",
                    },
                    "& .MuiDataGrid-columnHeader:focus-within": {
                      outline: "none !important",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      ".MuiDataGrid-menuIcon, .MuiDataGrid-sortIcon, .MuiDataGrid-columnHeader--sorted":
                        {
                          display: "none",
                          pointerEvents: "none",
                        },
                      cursor: "default",
                      paddingLeft: 1,
                      paddingRight: 1,
                      margin: 0,
                      backgroundColor: "#333",
                      color: "#fff",
                      borderBottom: "1px solid #ccc",
                    },
                    // Additionally target specific sort button
                    "& .MuiDataGrid-columnHeader--sortable": {
                      pointerEvents: "none",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                      padding: 0, // Adjust as needed
                      margin: 0, // Adjust as needed
                    },
                    "& .MuiDataGrid-columnHeadersInner": {
                      width: "100%", // Adjust as needed
                      padding: 0,
                    },
                    border: "1px solid #FCF2F0", // Change the border color of the entire DataGrid
                    "& .MuiDataGrid-cell": {
                      borderColor: "#333333", // Change cell border color
                    },
                    "& .MuiDataGrid-columnSeparator": {
                      display: "none", // Optional: Hide the column separators
                    },
                    ".MuiDataGrid-cell:focus-within": {
                      outline: "none !important",
                    },
                    "& .MuiDataGrid-row": {
                      cursor: "pointer", // Change cursor to pointer for rows
                      // backgroundColor: "#fff4c0 !important",
                    },
                    ...(isMobileRequest
                      ? {
                          "& .MuiDataGrid-columnHeaders": {
                            display: "none",
                          },
                          "& .MuiDataGrid-virtualScroller": {
                            overflowY: "auto",
                            WebkitOverflowScrolling: "touch",
                            touchAction: "pan-y",
                          },
                          "& .MuiDataGrid-footerContainer": {
                            flex: "0 0 auto",
                          },
                          "& .MuiDataGrid-cell": {
                            py: 0,
                            paddingLeft: 0,
                            paddingRight: 2.5,
                          },
                        }
                      : {}),
                  }}
                  rows={permitPackages || []}
                  columns={
                    isMobileRequest
                      ? permitColumnsSmallByTab
                      : notarizationRequestsColumn
                  }
                  getRowId={(row) => row?.id}
                  onPaginationModelChange={(model, details) => {
                    handlePaginationModelChange(model, details);
                  }}
                  loading={dataLoading}
                  paginationMode="server"
                  paginationModel={{
                    page: page, // Set the current page value here
                    pageSize: pageSize, // Set the page size value here
                  }}
                  rowCount={typeof rowCount === "number" ? rowCount : 0}
                  initialState={{
                    pagination: {
                      paginationModel: {
                        pageSize: 5,
                      },
                    },
                  }}
                  onRowClick={(params, event, details) => {
                    handleRowClick(params, event, details);
                  }}
                  pageSizeOptions={[5]}
                  rowHeight={isMobileRequest ? 100 : tabletSize ? 104 : 34}
                  disableColumnFilter={tabletSize}
                />
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              {!selectedRow ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    width: "100%",
                    minHeight: "200px",
                  }}
                >
                  <Typography
                    sx={{ textAlign: "center", px: 2 }}
                    variant={mobileSize ? "h6" : "h5"}
                    color="primary"
                  >
                    {permitPackages?.length > 0
                      ? packageRowDetailsEmptyTitle?.[activeTab]
                      : ""}
                  </Typography>
                </Box>
              ) : (
                <Grid
                  container
                  sx={{ width: "100%", height: "100%", alignItems: "stretch" }}
                >
                  <Grid
                    size={{ xs: 12 }}
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 0,
                      overflow: "hidden",
                      flex: 1,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontStyle: "italic",
                      }}
                      align="center"
                      mt={0.2}
                      py={0.5}
                    >
                      Permit Package Documents
                    </Typography>

                    {!selectedRow?.cst_packageID && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          px: 3,
                          py: 0,
                          borderBottom: "1px solid rgba(0,0,0,0.08)",
                          backgroundColor: "rgba(0,0,0,0.02)",
                        }}
                      >
                        <LoadingButton
                          variant="text"
                          endIcon={<DownloadIcon sx={{ fontSize: 18 }} />}
                          size="medium"
                          loading={ppDownloading}
                          onClick={() =>
                            dispatch(handleDownloadNoc("all", "legal"))
                          }
                          sx={{
                            fontWeight: 500,
                            textTransform: "none",
                            color: "primary.main",
                            px: 2,
                            py: 1,
                            "&:hover": {
                              backgroundColor: "rgba(25, 118, 210, 0.04)",
                            },
                          }}
                        >
                          Download Package
                        </LoadingButton>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: "0.875rem",
                            color: "text.secondary",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 400,
                              color: "text.secondary",
                              "& .number": {
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: "0.9rem",
                              },
                            }}
                          >
                            Forms{" "}
                            <Box component="span" className="number">
                              {numCreated || 0}
                            </Box>
                            <Box
                              component="span"
                              sx={{ mx: 0.5, opacity: 0.6 }}
                            >
                              /
                            </Box>
                            <Box component="span" className="number">
                              {selectedRow?.selected_documents?.length || 0}
                            </Box>
                          </Typography>

                          <Box
                            sx={{
                              width: "1px",
                              height: "16px",
                              backgroundColor: "divider",
                              opacity: 0.4,
                            }}
                          />

                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 400,
                              color: "text.secondary",
                              "& .number": {
                                fontWeight: 600,
                                color: "text.primary",
                                fontSize: "0.9rem",
                              },
                            }}
                          >
                            Permits{" "}
                            <Box component="span" className="number">
                              1
                            </Box>
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* LEFT CONTENT STARTS */}
                    <Box
                      sx={{
                        display: "flex",
                        height: isMobileRequest ? "100%" : "calc(100% - 90px)",
                        flex: isMobileRequest ? 1 : undefined,
                        minHeight: 0,
                        mt: 0,
                        borderTop: "4px solid #d3d3d3",
                        flexDirection: isMobileRequest ? "column" : "row",
                      }}
                    >
                      {isMobileRequest ? (
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 0,
                          }}
                        >
                          <Box
                            sx={{
                              px: 1,
                              py: 1,
                              borderBottom: "1px solid rgba(0,0,0,0.08)",
                              backgroundColor: "#fff",
                              flex: "0 0 auto",
                            }}
                          >
                            <ToggleButtonGroup
                              value={mobileDetailsView}
                              exclusive
                              fullWidth
                              size="small"
                              onChange={(_, next) => {
                                if (next) {
                                  setMobileDetailsView(next);
                                }
                              }}
                              sx={{
                                backgroundColor: "#f7f7fb",
                                borderRadius: 2,
                                p: 0.3,
                                "& .MuiToggleButton-root": {
                                  border: "none",
                                  textTransform: "none",
                                  fontWeight: 700,
                                  flex: 1,
                                  py: 0.75,
                                },
                                "& .Mui-selected": {
                                  backgroundColor: "#fff",
                                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                },
                              }}
                            >
                              {!selectedRow?.cst_packageID && (
                                <ToggleButton value="docs">Docs</ToggleButton>
                              )}
                              <ToggleButton value="status">Status</ToggleButton>
                              <ToggleButton value="details">
                                Details
                              </ToggleButton>
                            </ToggleButtonGroup>
                          </Box>

                          {/* scrollable panel */}
                          <Box
                            sx={{
                              height: "100%",
                              minHeight: 0,
                              backgroundColor: "#fff",
                            }}
                          >
                            {!selectedRow ? null : (
                              <>
                                {mobileDetailsView === "docs" && (
                                  <Box sx={{ width: "100%" }}>
                                    {mobileDocTabs}
                                  </Box>
                                )}

                                {mobileDetailsView === "status" && (
                                  <Box sx={{ width: "100%" }}>
                                    {middleContent}
                                  </Box>
                                )}

                                {mobileDetailsView === "details" && (
                                  <Box sx={{ width: "100%" }}>
                                    {memoizedRightContent}
                                  </Box>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      ) : (
                        <>
                          {selectedRow?.cst_packageID ? (
                            <></>
                          ) : (
                            <Box
                              className="step14"
                              sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                height: "100%",
                                maxHeight: "100%",
                                flexDirection: "column",
                                alignSelf: "stretch",
                                minHeight: 0,
                                overflowY: "auto",
                                maxWidth: { xs: "16rem", xl: "unset" },
                                minWidth: { xs: "unset", xl: "16rem" },
                              }}
                            >
                              <Tabs
                                orientation="vertical"
                                value={leftValue}
                                onChange={handleLeftChange}
                                variant="scrollable"
                                scrollButtons="auto"
                                allowScrollButtonsMobile
                                aria-label="scrollable auto tabs example"
                                slotProps={{
                                  indicator: {
                                    sx: {
                                      left: 0, // Position the indicator on the left side
                                      width: "3px", // Adjust the width of the indicator
                                    },
                                  },
                                }}
                                sx={{
                                  alignItems: "flex-start", // Align the tabs to the left
                                  flex: 1,
                                  minHeight: 0,
                                  maxHeight: "100%",
                                  overflowY: "auto",
                                }}
                              >
                                {selectedRow?.selected_documents?.map(
                                  (document, index) => {
                                    const isEnabled =
                                      selectedRow?.permit_package_created ||
                                      document?.created ||
                                      (selectedRow?.selected_documents[
                                        index - 1
                                      ]?.created &&
                                        !document?.created);

                                    return (
                                      <Tab
                                        disabled={!isEnabled}
                                        key={index}
                                        label={
                                          <span
                                            style={{
                                              display: "inline-flex",
                                              alignItems: "flex-start",
                                              textAlign: "left",
                                            }}
                                          >
                                            {document.label}
                                            {selectedRow?.selected_documents?.[
                                              index
                                            ]?.created &&
                                            selectedRow?.selected_documents?.[
                                              index
                                            ]?.name === "noc" &&
                                            (selectedRow?.notarized === true ||
                                              selectedRow?.noc_notarized ===
                                                true) ? (
                                              <CheckCircleIcon
                                                style={{
                                                  color: green[500],
                                                  marginLeft: 8,
                                                  fontSize: "20px",
                                                }}
                                              />
                                            ) : selectedRow
                                                ?.selected_documents?.[index]
                                                ?.name !== "noc" &&
                                              (selectedRow?.notarized ===
                                                true ||
                                                selectedRow?.pp_notarized ===
                                                  true) ? (
                                              <CheckCircleIcon
                                                style={{
                                                  color: green[500],
                                                  marginLeft: 8,
                                                  fontSize: "20px",
                                                }}
                                              />
                                            ) : selectedRow?.processing_permit?.notarization?.final_documents?.find(
                                                (doc) =>
                                                  doc?.name ===
                                                  selectedRow
                                                    ?.selected_documents?.[
                                                    index
                                                  ]?.name,
                                              ) ? (
                                              <CheckCircleIcon
                                                style={{
                                                  color: green[500],
                                                  marginLeft: 8,
                                                  fontSize: "20px",
                                                }}
                                              />
                                            ) : selectedRow
                                                ?.selected_documents?.[index]
                                                ?.created ? (
                                              <CheckCircleIcon
                                                style={{
                                                  color: grey[500],
                                                  marginLeft: 8,
                                                  fontSize: "20px",
                                                }}
                                              />
                                            ) : (
                                              <></>
                                            )}
                                          </span>
                                        }
                                        disableRipple
                                        sx={{
                                          py: 0,
                                          alignItems: "flex-start", // Align the tabs to the left
                                          marginLeft: "24px",
                                          ".MuiTab-root": {
                                            justifyContent: "flex-start", // Align the tab content to the left
                                            textAlign: "left", // Align the text to the left
                                            // Set a consistent left margin for all tabs
                                          },
                                        }}
                                      />
                                    );
                                  },
                                )}
                              </Tabs>
                            </Box>
                          )}

                          {/* MIDDLE CONTENT */}
                          {middleContent}
                          {/* MIDDLE CONTENT ENDS */}
                        </>
                      )}
                    </Box>
                  </Grid>

                  {/* RIGHT CONTENT */}
                  {!isMobileRequest && memoizedRightContent}
                </Grid>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
      <SelectDownloadFileSIzeModal
        open={fileSizeModalOpen}
        setOpen={setFileSizeModalOpen}
      />
      <Snackbar
        sx={{ zIndex: 9999999999999 }}
        open={myPermitsSnackOpen}
        autoHideDuration={5000}
        onClose={snackHandleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={snackHandleClose}
          severity="success" // Changed severity to success
          sx={{
            width: "100%",
            marginTop: "80px",
            marginLeft: lg ? "280px" : "0px",
            backgroundColor: "#388e3c", // Green shade for success
            color: "#fff",
            fontWeight: "bold",
          }}
          iconMapping={{
            success: <CheckCircleIcon sx={{ color: "#fff" }} />, // Changed to success icon
          }}
        >
          {myPermitsSnackText}
        </Alert>
      </Snackbar>
    </>
  );
};

export default React.memo(NotarizationRequestsGrid);
